import React, { useState, useEffect, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import useWindowDimensions from "../../../../util/windowUtils.ts";
import { BASE_WINDOW_WIDTH } from "../../../../paths/games/cardComponent.tsx";
import { useCardScale } from "../../../../util/cardUtils.ts";

interface NumberSelectorProps {
    min?: number;
    max?: number;
    initialValue?: number;
    onSubmit: (value: number) => void;
    visible: boolean;
    title?: string;
}

export const NumberSelector: React.FC<NumberSelectorProps> = ({
    min = 0,
    max = 10,
    initialValue = min,
    onSubmit,
    visible,
    title = "Select Number",
}) => {
    const scale = useCardScale();

    const [value, setValue] = useState<number>(initialValue);

    useEffect(() => {
        if (visible) {
            setValue(Math.max(min, Math.min(max, initialValue)));
        }
    }, [visible, min, max, initialValue]);

    const clamp = useCallback((v: number): number => {
        return Math.max(min, Math.min(max, v));
    }, [min, max]);

    const handleIncrement = () => {
        setValue(prev => clamp(prev + 1));
    };

    const handleDecrement = () => {
        setValue(prev => clamp(prev - 1));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (input === "") {
            setValue(min);
            return;
        }
        const num = parseInt(input, 10);
        if (!isNaN(num)) {
            setValue(clamp(num));
        }
    };

    const handleBlur = () => {
        setValue(prev => clamp(prev));
    };

    const handleSubmit = () => {
        onSubmit(clamp(value));
    };

    if (!visible) return null;

    const modalSize = {
        width: 500,
        padding: 16,
        gap: 12,
    };

    const fontSize = {
        title: 18,
        number: 32,
        button: 14,
    };

    const buttonSize = 48

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    width: `${modalSize.width}px`,
                    backgroundColor: "#1f2937",
                    borderRadius: `12px`,
                    padding: `${modalSize.padding}px`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: `${modalSize.gap}px`,
                    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.5)`,
                }}
            >
                <div
                    style={{
                        color: "#e5e7eb",
                        fontSize: `${fontSize.title}px`,
                        fontWeight: "bold",
                    }}
                >
                    {title}
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: `${modalSize.gap}px`,
                    }}
                >
                    <Button
                        variant="secondary"
                        onClick={handleDecrement}
                        style={{
                            width: `${buttonSize}px`,
                            height: `${buttonSize}px`,
                            fontSize: `${fontSize.button}px`,
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                        }}
                    >
                        −
                    </Button>

                    <Form.Control
                        value={value}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        min={min}
                        max={max}
                        style={{
                            width: `80px`,
                            height: `${buttonSize}px`,
                            fontSize: `${fontSize.number}px`,
                            fontWeight: "bold",
                            textAlign: "center",
                            backgroundColor: "#374151",
                            color: "#e5e7eb",
                            border: "none",
                            borderRadius: `8px`,
                        }}
                    />

                    <Button
                        variant="secondary"
                        onClick={handleIncrement}
                        style={{
                            width: `${buttonSize}px`,
                            height: `${buttonSize}px`,
                            fontSize: `${fontSize.button}px`,
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                        }}
                    >
                        +
                    </Button>
                </div>

                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    style={{
                        width: "100%",
                        height: `${buttonSize}px`,
                        fontSize: `${fontSize.button}px`,
                    }}
                >
                    Submit
                </Button>
            </div>
        </div>
    );
};